import {
  ApiBlueprint,
  configApiRef,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { googleAuthApiRef } from '@backstage/core-plugin-api';
import { GoogleAuth } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import type { IdentityProviders } from '@backstage/core-components';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';

/**
 * Google Workspace is the identity provider, matching every other internal app
 * (mic, mentor-match, compiq, pipeline) and the org standard that Workspace is
 * the one IdP.
 *
 * There is no custom API extension here on purpose. `googleAuthApiRef` is backed
 * by `api:app/google-auth`, which ships enabled in the app defaults — the Gitea
 * provider needed a hand-rolled OAuth2 factory only because the generic OIDC
 * provider has no built-in apiRef.
 */
const googleProvider = {
  id: 'google-auth-provider',
  title: 'AnswerRocket',
  message: 'Sign in with your AnswerRocket account',
  apiRef: googleAuthApiRef,
};

/**
 * Sign-in page.
 *
 * Google is the only real provider. Guest is offered as well when
 * `auth.enableGuestSignIn` is set, which exists so the app can be run at all
 * without an identity provider to hand — `auth.providers` holds only guest in
 * the base config, so a plain `yarn start` otherwise lands here with nothing to
 * click.
 *
 * Guest cannot reach a deployed environment: app-config.production.yaml sets the
 * flag to false, and the backend's guest provider independently refuses to issue
 * a token unless NODE_ENV=development.
 */
const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => {
      const configApi = useApi(configApiRef);

      // Deployed environments keep the single-provider form, which SignInPage
      // renders as one centred card. A one-element `providers` array instead
      // renders the multi-provider grid — left-aligned, and a gratuitous change
      // to a page every user sees.
      if (!configApi.getOptionalBoolean('auth.enableGuestSignIn')) {
        return <SignInPage {...props} provider={googleProvider} />;
      }

      const providers: IdentityProviders = ['guest', googleProvider];
      return <SignInPage {...props} providers={providers} />;
    },
  },
});

/**
 * The stock `api:app/google-auth`, restated so `control.preview` can swap it.
 *
 * With preview on (local development only — see the backend's
 * modules/control/preview), a Guest session has no Google identity, and every
 * Control-backed page asks for one before it calls the backend. The stand-in
 * hands those pages an UNSIGNED token naming guest@answerrocket.com: the
 * preview backend never reads it, and real Control or launchpad-api would
 * reject it on the signature. The backend refuses to start with preview set in
 * a production build, so this branch is unreachable there.
 *
 * Without preview the factory is the default one, verbatim.
 */
const previewIdToken = [
  { alg: 'none', typ: 'JWT' },
  { email: 'guest@answerrocket.com', name: 'Guest (preview)' },
]
  .map(part => btoa(JSON.stringify(part)).replace(/=+$/, ''))
  .concat('')
  .join('.');

const googleAuthApi = ApiBlueprint.make({
  name: 'google-auth',
  params: defineParams =>
    defineParams({
      api: googleAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) => {
        const real = GoogleAuth.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getOptionalString('auth.environment'),
        });
        if (!configApi.getOptionalBoolean('control.preview')) return real;
        return Object.assign(Object.create(real), {
          getIdToken: async () => previewIdToken,
        });
      },
    }),
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [signInPage, googleAuthApi],
});
